import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { PlusIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { useFinancialAccounts } from '../../../hooks/queries/useFinancialAccounts';
import { PATHS } from '../../../paths';
import { useNavigateWithDateRange } from '../../../providers/DateRangeProvider';
import { groupAccountsBySubtype, isSupportedFinancialAccount, supportedSubtypes } from '../../../utils/accounts';
import AccountGroup from '../../AccountGroup';
import PlaidLinkButton from '../../plaid/PlaidLinkButton';
import SyncStatus from '../../SyncStatus';
import Widget from '../../Widget';

/**
 * Right-column widget on the dashboard showing the user's financial accounts
 * grouped by subtype (checking, savings, credit cards). Includes a sync status
 * indicator, a "connect" button via Plaid Link, and navigates to the account
 * detail pane on click.
 */
const DashboardAccountsWidget = () => {
  const navigate = useNavigateWithDateRange();
  const { financialAccounts, loading } = useFinancialAccounts();

  const filteredAccounts = React.useMemo(
    () => financialAccounts?.filter(isSupportedFinancialAccount) ?? [],
    [financialAccounts]
  );

  const groupedAccounts = React.useMemo(() => groupAccountsBySubtype(filteredAccounts), [filteredAccounts]);

  // Find the most recent sync time across all accounts
  const mostRecentSync = React.useMemo(() => {
    const syncTimes = filteredAccounts
      .map((account) => account.bankConnection?.lastSyncedAt)
      .filter((time): time is string => !!time)
      .map((time) => new Date(time).getTime());

    if (syncTimes.length === 0) {
      return null;
    }
    return new Date(Math.max(...syncTimes)).toISOString();
  }, [filteredAccounts]);

  const handleAccountClick = (accountId: string) => navigate(PATHS.ACCOUNT(accountId));

  const headerTitle = (
    <div className="flex flex-col gap-0.5">
      <span>Accounts</span>
      <SyncStatus lastSyncedAt={mostRecentSync} addPrefix size="xs" />
    </div>
  );

  const addButton = <PlaidLinkButton color="tertiary" size="sm" iconLeading={<PlusIcon className="size-5" />} />;

  if (loading) {
    return (
      <Widget>
        <Widget.Header title="Accounts" />
        <Widget.Body>
          <LoadingIndicator className="h-[200px]" />
        </Widget.Body>
      </Widget>
    );
  }

  if (filteredAccounts.length === 0) {
    return null;
  }

  return (
    <Widget>
      <Widget.Header title={headerTitle} trailing={addButton} />
      <Widget.Body className="pb-2">
        {supportedSubtypes.map((subtype) => {
          const accounts = groupedAccounts[subtype] || [];
          if (accounts.length === 0) {
            return null;
          }

          return (
            <AccountGroup
              key={subtype}
              subtype={subtype}
              accounts={accounts}
              onAccountClick={handleAccountClick}
              compact
            />
          );
        })}
      </Widget.Body>
    </Widget>
  );
};

export default DashboardAccountsWidget;
